import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { BsActivity, BsChevronDown, BsChevronUp } from 'react-icons/bs';
import {
    FiEye, FiEdit2, FiPlusCircle, FiTrash2, FiGitMerge,
    FiAlertTriangle, FiMessageSquare, FiCornerDownRight,
} from 'react-icons/fi';
import { baseURL } from '../../../helpers/baseURL';
import { headers } from '../../../helpers/Requests';
import { emphasizeClaritaskNoteActor } from '../../../helpers/claritaskCommentFiles';
import './ActivityFeed.css';

// ─── helpers ────────────────────────────────────────────────────────────────

function fmtDate(iso) {
    const d = new Date(iso);
    return {
        month: d.toLocaleString('en-US', { month: 'short' }),
        day:   d.getDate(),
        year:  d.getFullYear(),
        time:  d.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }),
    };
}

function capitalize(str = '') {
    if (!str) return str;
    return str.charAt(0).toUpperCase() + str.slice(1);
}

function stripHtml(html = '') {
    return String(html)
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/\s+/g, ' ')
        .trim();
}

function truncate(str = '', max = 60) {
    const s = String(str);
    return s.length > max ? s.slice(0, max).trim() + '…' : s;
}

function prettyValue(value, field) {
    if (value === null || value === undefined || value === '') return null;
    let v = String(value);
    if (field === 'description') v = stripHtml(v);
    else                         v = capitalize(v);
    return v;
}

function Val({ children }) {
    if (children === null || children === undefined || children === '')
        return <em className="act-val-empty">—</em>;
    return <strong className="act-val">{String(children)}</strong>;
}

function Arrow() {
    return <span className="act-arrow">→</span>;
}

// ─── collapsible multi-field block ───────────────────────────────────────────

function CollapsibleFields({ fieldNames, children }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="act-collapsible">
            <button className="act-collapsible-toggle" onClick={() => setOpen(o => !o)}>
                <span className="act-collapsible-fields">
                    {fieldNames.map((f, i) => (
                        <React.Fragment key={f}>
                            {i > 0 && <span className="act-collapsible-sep">·</span>}
                            <span className="act-collapsible-chip">{f}</span>
                        </React.Fragment>
                    ))}
                </span>
                <span className="act-collapsible-caret">
                    {open ? <BsChevronUp /> : <BsChevronDown />}
                </span>
            </button>
            {open && <div className="act-collapsible-body">{children}</div>}
        </div>
    );
}

// ─── expandable text ─────────────────────────────────────────────────────────

function ExpandableText({ text, html = false }) {
    const [expanded, setExpanded] = useState(false);
    const ref = useRef(null);
    const [overflows, setOverflows] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (el) setOverflows(el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight);
    }, [text]);

    return (
        <span className="act-expandable">
            <span
                ref={ref}
                className={`act-expandable-text${expanded ? ' is-expanded' : ''}`}
                {...(html ? { dangerouslySetInnerHTML: { __html: text } } : { children: text })}
            />
            {(overflows || expanded) && (
                <button className="act-see-more" onClick={() => setExpanded(e => !e)}>
                    {expanded ? 'see less' : 'see more'}
                </button>
            )}
        </span>
    );
}

// ─── description-string parser ───────────────────────────────────────────────

const ADD_REM_RE  = /^updated ticket (.+?): (added|removed) '([\s\S]*?)'\s*$/i;
const FROM_TO_RE  = /^updated ticket (.+?) from '([\s\S]*?)' to '([\s\S]*?)'\s*$/i;

// Known field names — used as segment boundaries in multi-field DB strings.
const KNOWN_FIELDS = ['title', 'description', 'status', 'priority', 'type', 'department', 'due date', 'assignees', 'tags'];
const FIELD_PATTERN = KNOWN_FIELDS.join('|');

// Finds every "fieldName from '..." segment start in a stripped body string.
// Boundaries are: start-of-string, OR a closing quote followed by whitespace then a field name.
// We use the regex match index + the length of any prefix chars to land exactly on the field name.
const BOUNDARY_RE = new RegExp(`(?:^|'\\s+)(${FIELD_PATTERN})\\s+from\\s+'`, 'gi');


function parseMultiField(text) {
    const body = text.replace(/^updated ticket\s+/i, '');

    // Collect boundary positions — each entry is where a field segment starts.
    // Using the regex exec index + offset to the captured field name avoids
    // indexOf() false-matches when a field name appears inside a value.
    const boundaries = [];
    let m;
    BOUNDARY_RE.lastIndex = 0;
    while ((m = BOUNDARY_RE.exec(body)) !== null) {
        // m[0] is the full match e.g. "' description from '"
        // m[1] is the captured field name e.g. "description"
        // The field name starts at: m.index + (m[0].length - m[1].length - " from '".length)
        const prefixLen  = m[0].indexOf(m[1]);      // chars before the field name in m[0]
        const fieldIndex = m.index + prefixLen;
        boundaries.push({ field: m[1].toLowerCase().trim(), index: fieldIndex });
    }

    if (boundaries.length < 2) return [];

    const changes = [];
    for (let i = 0; i < boundaries.length; i++) {
        const segStart = boundaries[i].index;
        const segEnd   = i + 1 < boundaries.length ? boundaries[i + 1].index : body.length;
        const segment  = body.slice(segStart, segEnd).trim();

        // For the last segment there is no trailing boundary, so newVal runs to end-of-string.
        // Use a two-pass approach: match "field from 'old' to '" then take the rest as newVal.
        const openRE = new RegExp(`^(?:${FIELD_PATTERN})\\s+from\\s+'([\\s\\S]*?)'\\s+to\\s+'`, 'i');
        const openM  = segment.match(openRE);
        if (!openM) continue;

        const oldVal    = openM[1];
        const afterOpen = segment.slice(openM[0].length);          // everything after the opening "to '"
        const newVal    = afterOpen.endsWith("'")
            ? afterOpen.slice(0, -1)                               // strip the trailing closing quote
            : afterOpen;                                           // no closing quote (shouldn't happen)

        changes.push({ field: boundaries[i].field, oldVal, newVal });
    }
    return changes;
}

function parseDescription(description = '') {
    const text = String(description).trim();

    if (/^ticket viewed$/i.test(text)) return { kind: 'viewed' };

    const addRem = text.match(ADD_REM_RE);
    if (addRem) return {
        kind: 'list',
        field:  addRem[1].toLowerCase().trim(),
        action: addRem[2].toLowerCase(),
        value:  addRem[3],
    };

    // Multi-field MUST be tried before single-field: FROM_TO_RE would partially
    // match the beginning of a multi-field string and win, hiding the rest.
    const multiChanges = parseMultiField(text);
    if (multiChanges.length > 1) return { kind: 'multi', changes: multiChanges };

    const fromTo = text.match(FROM_TO_RE);
    if (fromTo) return {
        kind:   'change',
        field:  fromTo[1].toLowerCase().trim(),
        oldVal: fromTo[2],
        newVal: fromTo[3],
    };

    return { kind: 'raw', text: capitalize(text) };
}


// ─── label icon map ─────────────────────────────────────────────────────────

const LABEL_ICONS = {
    'Viewed':  FiEye,
    'Created': FiPlusCircle,
    'Updated': FiEdit2,
    'Deleted': FiTrash2,
    'Merged':  FiGitMerge,
    'Warning': FiAlertTriangle,
    'Note':    FiMessageSquare,
    'Reply':   FiCornerDownRight,
};

function LabelIcon({ label }) {
    const Icon = LABEL_ICONS[label];
    if (!Icon) return null;
    return <Icon className="act-label-icon" />;
}

function fieldVisuals(field) {
    switch (field) {
        case 'status':      return { label: 'Status' };
        case 'priority':    return { label: 'Priority' };
        case 'title':       return { label: 'Title' };
        case 'description': return { label: 'Description' };
        case 'assignees':   return { label: 'Assignees' };
        case 'tags':        return { label: 'Tags' };
        case 'department':  return { label: 'Department' };
        case 'type':        return { label: 'Type' };
        case 'due date':    return { label: 'Due Date' };
        default:            return { label: capitalize(field) };
    }
}

// ─── per-event renderer ──────────────────────────────────────────────────────

function actorName(activity) {
    const props = activity?.properties ?? {};
    const label = typeof props.actor_label === 'string' ? props.actor_label.trim()
        : (typeof props.actor === 'string' ? props.actor.trim() : '');
    if (label) return label;
    const name = activity?.causer?.name;
    if (typeof name === 'string' && name.trim()) return name.trim();
    return 'Someone';
}

function renderBody(activity) {
    const who   = actorName(activity);
    const ev    = activity.event;
    const attrs = activity.properties?.attributes ?? {};

    // ── comment events ──
    if (activity.log_name === 'comment') {
        const isReply   = attrs.type === 'reply';
        const isUpdated = /updated/i.test(activity.description ?? '');
        const isDeleted = /deleted/i.test(activity.description ?? '');
        const typeLabel  = isReply ? 'reply' : 'note';
        const label      = isDeleted ? 'Deleted' : isUpdated ? 'Updated' : (isReply ? 'Reply' : 'Note');
        const actionText = isDeleted ? `deleted a ${typeLabel}` : isUpdated ? `updated a ${typeLabel}` : `added a ${typeLabel}`;

        const content    = !isDeleted && attrs.content ? emphasizeClaritaskNoteActor(attrs.content) : null;
        const oldContent = isUpdated && activity.properties?.old?.content ? emphasizeClaritaskNoteActor(activity.properties.old.content) : null;

        return {
            label,
            line: (oldContent || content) ? (
                <div className="act-multi-wrapper">
                    <span className="act-multi-intro"><Val>{who}</Val> {actionText}</span>
                    <CollapsibleFields fieldNames={[isReply ? 'Reply' : 'Note']}>
                        <div className="act-multi-list">
                            {isUpdated ? (
                                <>
                                    <div className="act-multi-row act-multi-row--desc">
                                        <span className="act-multi-field-label">Before</span>
                                        <ExpandableText text={oldContent || '—'} html />
                                    </div>
                                    <div className="act-multi-row act-multi-row--desc">
                                        <span className="act-multi-field-label">After</span>
                                        <ExpandableText text={content || '—'} html />
                                    </div>
                                </>
                            ) : (
                                <div className="act-multi-row act-multi-row--desc act-multi-row--full">
                                    <ExpandableText text={content} html />
                                </div>
                            )}
                        </div>
                    </CollapsibleFields>
                </div>
            ) : (
                <><Val>{who}</Val> {actionText}</>
            ),
        };
    }

    // ── structural events ──
    if (ev === 'viewed' || /^ticket viewed$/i.test(activity.description ?? ''))
        return { label: 'Viewed', line: <><Val>{who}</Val> viewed this ticket</> };

    if (ev === 'created')
        return { label: 'Created', line: <><Val>{who}</Val> created this ticket</> };

    if (ev === 'deleted')
        return { label: 'Deleted', line: <><Val>{who}</Val> deleted this ticket</> };

    if (ev === 'merged') {
        const p         = activity.properties ?? {};
        const secTicket = p.secondary_ticket ?? {};
        const secId     = secTicket.id    ?? null;
        const secTitle  = secTicket.title ?? null;
        const secRef    = [secId ? `#${secId}` : null, secTitle ? `"${truncate(secTitle, 50)}"` : null]
                             .filter(Boolean).join(' ');
        return {
            label: 'Merged',
            line: <><Val>{who}</Val> merged ticket <Val>{secRef || '—'}</Val> into this ticket</>,
        };
    }

    if (ev === 'workspace_mismatch')
        return { label: 'Warning', line: <><Val>{who}</Val> triggered a workspace mismatch</> };

    // ── description-string events ──
    const parsed = parseDescription(activity.description ?? '');

    if (parsed.kind === 'viewed')
        return { label: 'Viewed', line: <><Val>{who}</Val> viewed this ticket</> };

    if (parsed.kind === 'multi') {
        const fieldNames = parsed.changes.map(c => fieldVisuals(c.field).label);
        return {
            label: 'Updated',
            line: (
                <div className="act-multi-wrapper">
                    <span className="act-multi-intro">
                        <Val>{who}</Val> updated {parsed.changes.length} field{parsed.changes.length !== 1 ? 's' : ''}
                    </span>
                    <CollapsibleFields fieldNames={fieldNames}>
                        <div className="act-multi-list">
                            {parsed.changes.map((change, i) => {
                                const { label: fLabel } = fieldVisuals(change.field);
                                const isDesc = change.field === 'description';
                                const oldV = isDesc ? (change.oldVal || '—') : (prettyValue(change.oldVal, change.field) || '—');
                                const newV = isDesc ? (change.newVal || '—') : (prettyValue(change.newVal, change.field) || '—');
                                const isLongText = ['description', 'title'].includes(change.field);

                                if (isLongText) {
                                    return (
                                        <React.Fragment key={i}>
                                            <div className="act-multi-row act-multi-row--desc act-multi-row--group-header">
                                                <span className="act-multi-field-label">{fLabel}</span>
                                            </div>
                                            <div className="act-multi-row act-multi-row--desc">
                                                <span className="act-multi-field-label">Before</span>
                                                <ExpandableText text={oldV} html={isDesc} />
                                            </div>
                                            <div className="act-multi-row act-multi-row--desc">
                                                <span className="act-multi-field-label">After</span>
                                                <ExpandableText text={newV} html={isDesc} />
                                            </div>
                                        </React.Fragment>
                                    );
                                }

                                return (
                                    <div key={i} className="act-multi-row">
                                        <span className="act-multi-field-label">{fLabel}</span>
                                        <span className="act-multi-old">{oldV}</span>
                                        <span className="act-multi-arrow">→</span>
                                        <span className="act-multi-new">{newV}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </CollapsibleFields>
                </div>
            ),
        };
    }

    if (parsed.kind === 'change') {
        if (parsed.field === 'description' || parsed.field === 'title') {
            const fLabel = fieldVisuals(parsed.field).label;
            return {
                label: 'Updated',
                line: (
                    <div className="act-multi-wrapper">
                        <span className="act-multi-intro"><Val>{who}</Val> updated {parsed.field}</span>
                        <CollapsibleFields fieldNames={[fLabel]}>
                            <div className="act-multi-list">
                                <div className="act-multi-row act-multi-row--desc">
                                    <span className="act-multi-field-label">Before</span>
                                    <ExpandableText text={parsed.oldVal || '—'} html={parsed.field === 'description'} />
                                </div>
                                <div className="act-multi-row act-multi-row--desc">
                                    <span className="act-multi-field-label">After</span>
                                    <ExpandableText text={parsed.newVal || '—'} html={parsed.field === 'description'} />
                                </div>
                            </div>
                        </CollapsibleFields>
                    </div>
                ),
            };
        }
        return {
            label: 'Updated',
            line: (
                <span className="act-change-line">
                    <Val>{who}</Val> updated {parsed.field} from{' '}
                    <Val>{prettyValue(parsed.oldVal, parsed.field) || '—'}</Val>{' '}
                    <Arrow />{' '}
                    <Val>{prettyValue(parsed.newVal, parsed.field) || '—'}</Val>
                </span>
            ),
        };
    }

    if (parsed.kind === 'list') {
        const verb = parsed.action === 'added' ? 'added' : 'removed';
        const prep = parsed.action === 'added' ? 'to' : 'from';
        return { label: 'Updated', line: <><Val>{who}</Val> {verb} <Val>{parsed.value}</Val> {prep} {parsed.field}</> };
    }

    // ── loose comment/note fallback ──
    if (activity.description?.toLowerCase().includes('note') || activity.description?.toLowerCase().includes('comment')) {
        const isReply   = activity.properties?.attributes?.type === 'reply';
        const isUpdated = /updated/i.test(activity.description ?? '');
        const isDeleted = /deleted/i.test(activity.description ?? '');
        const label      = isUpdated ? 'Updated' : (isReply ? 'Reply' : 'Note');
        const actionText = isUpdated ? `updated a ${isReply ? 'reply' : 'note'}` : `added a ${isReply ? 'reply' : 'note'}`;
        const content    = (!isDeleted && activity.properties?.attributes?.content) ? emphasizeClaritaskNoteActor(activity.properties.attributes.content) : null;
        return {
            label,
            line: content ? (
                <div className="act-multi-wrapper">
                    <span className="act-multi-intro"><Val>{who}</Val> {actionText}</span>
                    <CollapsibleFields fieldNames={[isReply ? 'Reply' : 'Note']}>
                        <div className="act-multi-list">
                            <div className="act-multi-row act-multi-row--desc act-multi-row--full">
                                <ExpandableText text={content} html />
                            </div>
                        </div>
                    </CollapsibleFields>
                </div>
            ) : (
                <><Val>{who}</Val> {actionText}</>
            ),
        };
    }

    return { label: ev ?? 'Event', line: <><Val>{who}</Val> {parsed.text || 'recorded activity'}</> };
}


// ─── ActivityFeed component ──────────────────────────────────────────────────

export default function ActivityFeed({ ticketId, containerRef, style }) {
    const [activityTimeline, setActivityTimeline] = useState({});
    const [isLoading, setIsLoading]               = useState(false);
    const hasMoreRef                              = useRef(true);
    const isLoadingRef                            = useRef(false);
    const offsetRef                               = useRef(0);

    const fetchTicketActivity = async (offset) => {
        const response = await axios.get(`${baseURL}ticket-activity/${ticketId}?offset=${offset}`, headers);
        return response?.data ?? {};
    };

    // useCallback gives a stable reference so addEventListener/removeEventListener
    // always refer to the exact same function instance.
    const loadMore = useCallback(() => {
        if (isLoadingRef.current || !hasMoreRef.current) return;
        isLoadingRef.current = true;
        setIsLoading(true);

        fetchTicketActivity(offsetRef.current)
            .then((data) => {
                setActivityTimeline((prev) => {
                    const next = { ...prev };
                    Object.keys(data).forEach((date) => {
                        if (next[date]) next[date].push(...data[date]);
                        else            next[date] = data[date];
                    });
                    return next;
                });

                const flat = Object.values(data).flat();
                offsetRef.current += flat.length;

                if (flat.length < 10) {
                    hasMoreRef.current = false;
                } else {
                    // If the container isn't scrollable yet, keep fetching.
                    setTimeout(() => {
                        const el = containerRef?.current;
                        if (el && el.scrollHeight <= el.clientHeight) loadMore();
                    }, 0);
                }
            })
            .catch((err) => {
                console.error('[ActivityFeed] fetch error:', err);
            })
            .finally(() => {
                setIsLoading(false);
                isLoadingRef.current = false;
            });
    }, [ticketId]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleScroll = useCallback(() => {
        const el = containerRef?.current;
        if (!el) return;
        if (hasMoreRef.current && el.scrollTop + el.clientHeight >= el.scrollHeight - 100) {
            loadMore();
        }
    }, [loadMore, containerRef]);

    useEffect(() => {
        // Reset all state when ticketId changes.
        offsetRef.current    = 0;
        hasMoreRef.current   = true;
        isLoadingRef.current = false;
        setActivityTimeline({});

        const el = containerRef?.current;
        if (el) el.addEventListener('scroll', handleScroll);
        loadMore();

        return () => {
            if (el) el.removeEventListener('scroll', handleScroll);
        };
    }, [ticketId, handleScroll, loadMore]); // containerRef is a stable ref object — safe to omit

    const items = Object.values(activityTimeline)
        .flat()
        .filter(a => a.log_name === 'ticket' || a.log_name === 'comment' || (a.log_name === 'default' && a.event === 'merged'));

    return (
        <div className="act-feed" style={{ flex: 1, minHeight: 0, ...style }}>

            {!isLoading && items.length === 0 && (
                <div className="act-empty">
                    <BsActivity className="act-empty-icon" />
                    <span className="act-empty-text">No activity recorded yet.</span>
                </div>
            )}

            <div className="act-timeline">
                {items.map((activity, idx) => {
                    const { label, line } = renderBody(activity);
                    const at  = fmtDate(activity.created_at);
                    const isLeft = idx % 2 === 0;

                    const card = (
                        <div className="act-card">
                            <div className="act-card-inner">
                                <div className="act-card-content">
                                    <div className="act-label">
                                        <LabelIcon label={label} />
                                        {label}
                                    </div>
                                    <div className="act-line">{line}</div>
                                </div>
                                <div className="act-date-stamp">
                                    <span className="act-month">{at.month}</span>
                                    <span className="act-day">{at.day}</span>
                                    <span className="act-year">{at.year}</span>
                                    <span className="act-time">{at.time}</span>
                                </div>
                            </div>
                        </div>
                    );

                    return (
                        <div key={activity.id} className="act-item">
                            <div className={`act-slot-left${isLeft ? '' : ' is-empty'}`}>
                                {isLeft ? card : null}
                            </div>
                            <div className="act-axis">
                                <div className="act-dot" />
                            </div>
                            <div className={`act-slot-right${!isLeft ? '' : ' is-empty'}`}>
                                {!isLeft ? card : null}
                            </div>
                        </div>
                    );
                })}
            </div>

            {isLoading && (
                <div className="act-loader">
                    <div className="act-spinner" />
                    Loading activity…
                </div>
            )}
        </div>
    );
}
